// src/components/Combobox.tsx
import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/app/global-components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/app/global-components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/global-components/popover";
import { LANG } from "@/types";
import { useLocale } from "@/middleware";

interface Option {
  value: string;
  label: string | React.ReactNode;
  icon?: string;
  description?: string;
}

interface ComboboxProps {
  options: Option[];
  value: string | null | number;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  showIcons?: boolean;
}

export function ComboboxView({
  options,
  value,
  disabled,
  onChange,
  placeholder,
  showIcons = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const { locale } = useLocale();

  const selectedOption = options.find((option) => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          disabled={disabled}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between relative w-full h-auto min-h-[40px] px-3 py-2",
            "hover:bg-gray-50 transition-colors duration-200",
            "border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          )}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {showIcons && selectedOption?.icon && (
              <span className="text-lg flex-shrink-0">
                {selectedOption.icon}
              </span>
            )}
            <span className={cn("truncate", !value && "text-gray-500")}>
              {value !== null ? selectedOption?.label : placeholder}
            </span>
          </div>
          <ChevronsUpDown
            className={cn(
              "ml-2 h-4 w-4 flex-shrink-0 opacity-50 transition-transform duration-200",
              open && "rotate-180",
              locale === LANG.AR ? "mr-2 ml-0" : "ml-2"
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          "p-0 !z-50 bg-white border border-gray-200 shadow-lg rounded-lg",
          "w-full max-w-[280px] max-h-[240px] overflow-hidden"
        )}
      >
        <Command className="!z-50">
          <CommandList className="!z-50 max-h-[240px] overflow-y-auto no-scrollbar">
            <CommandEmpty className="py-4 text-center text-gray-500">
              <div className="flex flex-col items-center gap-1">
                <span className="text-lg">🔍</span>
                <span className="text-sm">
                  No {placeholder?.toLowerCase()} found.
                </span>
              </div>
            </CommandEmpty>
            <CommandGroup className="p-1">
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    onChange(currentValue === value ? null : currentValue);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer",
                    "hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150",
                    "data-[selected=true]:bg-blue-100 data-[selected=true]:text-blue-700"
                  )}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {showIcons && option.icon && (
                      <span className="text-base flex-shrink-0">
                        {option.icon}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate text-sm">
                        {option.label}
                      </div>
                      {option.description && (
                        <div className="text-xs text-gray-500 truncate">
                          {option.description}
                        </div>
                      )}
                    </div>
                  </div>
                  <Check
                    className={cn(
                      "h-3 w-3 flex-shrink-0 text-blue-600",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
